import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Pencil, Trash2, Plus } from "lucide-react";
import { fmtDate } from "@/lib/dates";

export default function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/companies");
      setCompanies(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/companies/${editId}`, { name });
      else await api.post("/companies", { name });
      toast.success("Gemt");
      setOpen(false);
      setName("");
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Slet virksomhed?")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Slettet");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Virksomheder</h1>
          <p className="text-slate-500 mt-1">Administrer virksomheder i kontorhuset</p>
        </div>
        <Button
          onClick={() => {
            setName("");
            setEditId(null);
            setOpen(true);
          }}
          className="rounded-xl bg-slate-900 hover:bg-slate-800 gap-2 h-11"
          data-testid="new-company-button"
        >
          <Plus className="h-4 w-4" /> Ny virksomhed
        </Button>
      </div>

      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Navn</TableHead>
              <TableHead>Oprettet</TableHead>
              <TableHead className="w-32 text-right">Handlinger</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {companies.map((c) => (
              <TableRow key={c.id} data-testid={`company-row-${c.id}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-slate-500">{fmtDate(c.created_at)}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setName(c.name);
                      setEditId(c.id);
                      setOpen(true);
                    }}
                    data-testid={`edit-company-${c.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => remove(c.id)} data-testid={`delete-company-${c.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {companies.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-slate-400 py-10">
                  Ingen virksomheder endnu
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editId ? "Rediger virksomhed" : "Ny virksomhed"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Navn</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required className="h-11 rounded-xl" data-testid="company-name-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">
                Annullér
              </Button>
              <Button type="submit" className="rounded-xl bg-slate-900 hover:bg-slate-800" data-testid="save-company-button">
                Gem
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
